import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from './api';
import type { AICallLogEntry, GenerationRecordEntry, PaginatedResponse } from './types';
import { calcCostTWD, formatCostTWD, sumCostTWD } from './costUtils';

const GEN_TYPES = ['', 'quick-gen', 'style-transfer', 'agent-chat', 'assistant-chat', 'quick-gen-chat', 'admin-assistant-chat'];

// ── Pagination ───────────────────────────────────────────────────────────────

const Pagination: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (p: number) => void;
}> = ({ page, totalPages, total, onPageChange }) => (
  <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-400">
    <span>共 {total.toLocaleString()} 筆</span>
    <div className="flex gap-2">
      <button
        onClick={() => onPageChange(page - 1)} disabled={page <= 1}
        className="px-3 py-1 border dark:border-gray-600 rounded disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
      >上一頁</button>
      <span className="px-3 py-1">{page} / {totalPages}</span>
      <button
        onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
        className="px-3 py-1 border dark:border-gray-600 rounded disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
      >下一頁</button>
    </div>
  </div>
);

// ── Owner Autocomplete ────────────────────────────────────────────────────────

const OwnerAutocomplete: React.FC<{
  ownerMap: Record<string, string>;
  value: string;
  onChange: (displayValue: string, accountId: string) => void;
  onClear: () => void;
}> = ({ ownerMap, value, onChange, onClear }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const suggestions = value.trim()
    ? Object.entries(ownerMap).filter(([, name]) =>
        name.toLowerCase().includes(value.toLowerCase())
      )
    : [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        placeholder="依擁有者名稱篩選"
        value={value}
        onChange={e => { onChange(e.target.value, ''); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="border dark:border-gray-600 rounded px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded shadow-lg max-h-48 overflow-auto text-sm">
          {suggestions.map(([accountId, name]) => (
            <li
              key={accountId}
              onMouseDown={() => { onChange(name, accountId); setOpen(false); }}
              className="px-3 py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-800 dark:text-gray-200"
            >
              {name}
            </li>
          ))}
        </ul>
      )}
      {value && (
        <button
          type="button"
          onMouseDown={() => { onClear(); setOpen(false); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs"
        >✕</button>
      )}
    </div>
  );
};

// ── Sub-rows: AICallLog entries for one GenerationRecord ──────────────────────

const CallLogSubRows: React.FC<{
  logs: AICallLogEntry[];
}> = ({ logs }) => {
  const sorted = [...logs].sort((a, b) => a.seq - b.seq);
  return (
    <>
      {sorted.map(log => (
        <tr key={log._id} className="bg-blue-50/40 dark:bg-blue-900/10 border-l-4 border-blue-200 dark:border-blue-700">
          <td className="px-4 py-1.5" />
          <td className="px-4 py-1.5 text-xs text-gray-500 dark:text-gray-400">#{log.seq}</td>
          <td className="px-4 py-1.5 text-xs text-gray-600 dark:text-gray-400">{log.step}</td>
          <td className="px-4 py-1.5 text-xs text-gray-600 dark:text-gray-400">{log.modelName}</td>
          <td className="px-4 py-1.5 text-right text-xs text-gray-600 dark:text-gray-400">{log.inputTokens.toLocaleString()}</td>
          <td className="px-4 py-1.5 text-right text-xs text-gray-600 dark:text-gray-400">{log.outputTokens.toLocaleString()}</td>
          <td className="px-4 py-1.5 text-right text-xs text-gray-600 dark:text-gray-400">{log.thoughtTokens.toLocaleString()}</td>
          <td className="px-4 py-1.5 text-right text-xs text-blue-600 dark:text-blue-400">
            {calcCostTWD(log.modelName, log.inputTokens, log.outputTokens, log.thoughtTokens) ?? <span className="text-gray-400">—</span>}
          </td>
          <td className="px-4 py-1.5" />
        </tr>
      ))}
    </>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const GenerationRecords: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse<GenerationRecordEntry> | null>(null);
  const [ownerMap, setOwnerMap] = useState<Record<string, string>>({});
  const [logsMap, setLogsMap] = useState<Record<string, AICallLogEntry[]>>({});
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  // Filter state (committed on search)
  const [page, setPage] = useState(1);
  const [ownerNameInput, setOwnerNameInput] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [typeInput, setTypeInput] = useState('');
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');

  // Active filter state
  const [typeFilter, setTypeFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  useEffect(() => {
    adminApi.getAccountOwnerMap().then(setOwnerMap).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setLoading(true);
      setLogsMap({});
      setExpandedIds(new Set());
      try {
        const res = await adminApi.getGenerationRecords({
          page,
          accountId: selectedAccountId || undefined,
          type: typeFilter || undefined,
          startDate: startDateFilter || undefined,
          endDate: endDateFilter || undefined,
        });
        if (cancelled) return;
        setData(res);

        if (res.data.length > 0) {
          setLogsLoading(true);
          try {
            const logsRes = await adminApi.getAiCallLogsByRecords(res.data.map(r => r._id));
            if (cancelled) return;
            const map: Record<string, AICallLogEntry[]> = {};
            for (const log of logsRes.data) {
              if (!map[log.recordId]) map[log.recordId] = [];
              map[log.recordId].push(log);
            }
            setLogsMap(map);
          } catch {
            // silently ignore log fetch errors
          } finally {
            if (!cancelled) setLogsLoading(false);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, [page, selectedAccountId, typeFilter, startDateFilter, endDateFilter]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setTypeFilter(typeInput);
    setStartDateFilter(startDateInput);
    setEndDateFilter(endDateInput);
  };

  const handleClear = () => {
    setOwnerNameInput('');
    setSelectedAccountId('');
    setTypeInput('');
    setTypeFilter('');
    setStartDateInput('');
    setEndDateInput('');
    setStartDateFilter('');
    setEndDateFilter('');
    setPage(1);
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const hasFilter = ownerNameInput || typeFilter || startDateFilter || endDateFilter;

  return (
    <div>
      <h2 className="text-xl font-bold mb-5 text-gray-800 dark:text-gray-100">生成記錄</h2>

      <form onSubmit={handleFilter} className="flex gap-3 mb-5 flex-wrap items-center">
        <OwnerAutocomplete
          ownerMap={ownerMap}
          value={ownerNameInput}
          onChange={(name, accountId) => {
            setOwnerNameInput(name);
            setSelectedAccountId(accountId);
          }}
          onClear={() => { setOwnerNameInput(''); setSelectedAccountId(''); }}
        />
        <select
          value={typeInput} onChange={e => setTypeInput(e.target.value)}
          className="border dark:border-gray-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          {GEN_TYPES.map(t => <option key={t} value={t}>{t || '所有類型'}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDateInput}
            onChange={e => setStartDateInput(e.target.value)}
            className="border dark:border-gray-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <span className="text-gray-400 text-sm">—</span>
          <input
            type="date"
            value={endDateInput}
            onChange={e => setEndDateInput(e.target.value)}
            className="border dark:border-gray-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <button type="submit" className="bg-blue-600 text-white px-4 py-1.5 rounded text-sm hover:bg-blue-700">
          搜尋
        </button>
        {hasFilter && (
          <button type="button" onClick={handleClear} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            清除
          </button>
        )}
      </form>

      {loading ? (
        <div className="text-gray-500 dark:text-gray-400">載入中...</div>
      ) : data ? (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400 uppercase">
                <tr>
                  <th className="px-4 py-3 w-8" />
                  <th className="px-4 py-3 text-left">擁有者</th>
                  <th className="px-4 py-3 text-left">類型</th>
                  <th className="px-4 py-3 text-left">提示文字</th>
                  <th className="px-4 py-3 text-right">輸入 Token</th>
                  <th className="px-4 py-3 text-right">輸出 Token</th>
                  <th className="px-4 py-3 text-right">思考 Token</th>
                  <th className="px-4 py-3 text-right">
                    費用 (TWD)
                    {logsLoading && <span className="ml-1 text-gray-400 normal-case font-normal">計算中…</span>}
                  </th>
                  <th className="px-4 py-3 text-left">時間</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {data.data.map(rec => {
                  const expanded = expandedIds.has(rec._id);
                  const subLogs = logsMap[rec._id];
                  const cost = subLogs ? formatCostTWD(sumCostTWD(subLogs)) : null;

                  return (
                    <React.Fragment key={rec._id}>
                      <tr
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                        onClick={() => toggleExpand(rec._id)}
                      >
                        <td className="px-4 py-2 text-center text-gray-400 dark:text-gray-500 select-none">
                          {expanded ? '▼' : '▶'}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-700 dark:text-gray-300 max-w-24 truncate" title={rec.accountId}>
                          {ownerMap[rec.accountId] || rec.accountId}
                        </td>
                        <td className="px-4 py-2">
                          <span className="bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                            {rec.type}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400 max-w-xs truncate">
                          {rec.inputs?.textPrompt || '-'}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{(rec.totalUsage?.inputTokens ?? 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{(rec.totalUsage?.outputTokens ?? 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{(rec.totalUsage?.thoughtTokens ?? 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-right font-medium text-gray-800 dark:text-gray-200">
                          {cost ?? <span className="text-gray-400 dark:text-gray-500">—</span>}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {new Date(rec.createdAt).toLocaleString('zh-TW')}
                        </td>
                      </tr>
                      {expanded && subLogs && subLogs.length > 0 && (
                        <CallLogSubRows logs={subLogs} />
                      )}
                      {expanded && subLogs && subLogs.length === 0 && (
                        <tr className="bg-gray-50 dark:bg-gray-900/30">
                          <td colSpan={9} className="px-8 py-2 text-xs text-gray-400 dark:text-gray-500">無 AI 呼叫記錄</td>
                        </tr>
                      )}
                      {expanded && !subLogs && (
                        <tr className="bg-gray-50 dark:bg-gray-900/30">
                          <td colSpan={9} className="px-8 py-2 text-xs text-gray-400 dark:text-gray-500">載入中…</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {data.data.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">無記錄</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} totalPages={data.totalPages} total={data.total} onPageChange={p => setPage(p)} />
        </>
      ) : null}
    </div>
  );
};

export default GenerationRecords;
